import React, { useState, useEffect, useReducer, useContext } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import ContactAvatar from "../../components/ContactAvatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { socketConnection } from "../../services/socket";
import EmptyState from "../../components/EmptyState";

import {CSVLink} from "react-csv";
import { GetApp } from "@material-ui/icons";
import { useTheme, useMediaQuery } from "@material-ui/core";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const formatPhone = (number) => {
  if (!number) return "-";
  const digits = String(number).replace(/\D/g, "");
  const digitsWithoutDDI = digits.startsWith("55") ? digits.slice(2) : digits;
  if (digitsWithoutDDI.length === 11) {
    return digitsWithoutDDI.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  if (digitsWithoutDDI.length === 10) {
    return digitsWithoutDDI.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  return digitsWithoutDDI || "-";
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  // const handleSaveTicket = async contactId => {
  // 	if (!contactId) return;
  // 	setLoading(true);
  // 	try {
  // 		const { data: ticket } = await api.post("/tickets", {
  // 			contactId: contactId,
  // 			userId: user?.id,
  // 			status: "open",
  // 		});
  // 		history.push(`/tickets/${ticket.id}`);
  // 	} catch (err) {
  // 		toastError(err);
  // 	}
  // 	setLoading(false);
  // };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleOpenContactProfile = (contactId) => {
    history.push(`/contacts/${contactId}/profile`);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleimportContact = async () => {
    try {
      setLoading(true);
      await api.post("/contacts/import");
      toast.success("Contatos importados com sucesso!");
      history.go(0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={(e) =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleimportContact()
        }
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      <MainHeader>
        <Title>{i18n.t("contacts.title")}</Title>
        <MainHeaderButtonsWrapper
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              flexWrap: "wrap",
              gap: isMobile ? 8 : 8,
              width: "100%",
              justifyContent: isMobile ? "stretch" : "flex-end",
              alignItems: isMobile ? "stretch" : "center",
              boxSizing: "border-box"
            }}
          >
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            style={{ maxWidth: isMobile ? "100%" : 300 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => setConfirmOpen(true)}
            fullWidth={isMobile}
          >
            {i18n.t("contacts.buttons.import")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenContactModal}
            fullWidth={isMobile}
          >
            {i18n.t("contacts.buttons.add")}
          </Button>

          <CSVLink
            separator=";"
            filename="contatos-messageexpress.csv"
            data={contacts.map((c) => ({ name: c.name, number: c.number, email: c.email }))}
            onClick={() => toast.info("Exportando contatos...")}
            style={{ color: "inherit", display: "flex", alignSelf: isMobile ? "flex-end" : "auto" }}
          >
            <IconButton color="primary" size="small">
              <GetApp fontSize="small" />
            </IconButton>
          </CSVLink>

        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
        style={{
          width: "100%",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Table size="small" aria-label="tabela de contatos">
          {!isMobile && (
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{i18n.t("contacts.table.name")}</TableCell>
                <TableCell align="center">
                  {i18n.t("contacts.table.whatsapp")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contacts.table.email")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("contacts.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {isMobile ? (
              contacts.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5} style={{ border: "none", padding: 0 }}>
                    <EmptyState
                      icon="👥"
                      title="Nenhum contato ainda"
                      description="Importe sua lista de contatos ou aguarde clientes entrarem em contato via WhatsApp."
                      actionLabel="+ Novo Contato"
                      onAction={handleOpenContactModal}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell 
                      colSpan={5} style={{
                        padding: 0,
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "12px 16px",
                          margin: "4px 0",
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: "8px",
                          backgroundColor: theme.palette.background.paper,
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                      >
                        <ContactAvatar
                          src={contact.profilePicUrl}
                          name={contact.name}
                          style={{ marginRight: 12, width: 40, height: 40 }}
                        />
                        <div
                          style={{ flex: 1, cursor: "pointer" }}
                          onClick={() => handleOpenContactProfile(contact.id)}
                        >
                          <div style={{ fontWeight: "bold", color: theme.palette.text.primary }}>{contact.name}</div>
                          <div style={{ fontSize: 14, color: theme.palette.text.secondary }}>
                            📱 {formatPhone(contact.number)}
                          </div>
                          <div style={{ fontSize: 14, color: theme.palette.text.secondary }}>
                            ✉️ {contact.email || "-"}
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenContactProfile(contact.id)}
                            title="Ver histórico"
                          >
                            <AssignmentIndIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setContactTicket(contact);
                              setNewTicketModalOpen(true);
                            }}
                            title="Abrir conversa"
                          >
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => hadleEditContact(contact.id)}
                            title="Editar"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <Can
                            role={user.profile}
                            perform="contacts-page:deleteContact"
                            yes={() => (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setConfirmOpen(true);
                                  setDeletingContact(contact);
                                }}
                                title="Deletar"
                                style={{ color: "#f44336" }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )
            ) : (
              <>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell padding="checkbox">
                      <ContactAvatar src={contact.profilePicUrl} name={contact.name} />
                    </TableCell>
                    <TableCell
                      style={{ cursor: "pointer" }}
                      onClick={() => handleOpenContactProfile(contact.id)}
                    >
                      {contact.name}
                    </TableCell>
                    <TableCell align="center">{formatPhone(contact.number)}</TableCell>
                    <TableCell align="center">{contact.email || "-"}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenContactProfile(contact.id)}
                        title="Ver histórico"
                      >
                        <AssignmentIndIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setContactTicket(contact);
                          setNewTicketModalOpen(true);
                        }}
                        title="Abrir conversa"
                      >
                        <WhatsAppIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => hadleEditContact(contact.id)}
                        title="Editar contato"
                      >
                        <EditIcon />
                      </IconButton>
                      <Can
                        role={user.profile}
                        perform="contacts-page:deleteContact"
                        yes={() => (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setConfirmOpen(true);
                              setDeletingContact(contact);
                            }}
                            title="Deletar contato"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {contacts.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} style={{ border: "none", padding: 0 }}>
                      <EmptyState
                        icon="👥"
                        title="Nenhum contato ainda"
                        description="Importe sua lista de contatos ou aguarde clientes entrarem em contato via WhatsApp."
                        actionLabel="+ Novo Contato"
                        onAction={handleOpenContactModal}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {loading && <TableRowSkeleton avatar columns={3} />}
                {!loading && !hasMore && contacts.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" style={{ color: "gray", fontSize: 12 }}>
                      🟢 Todos os contatos carregados
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>  
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Contacts;
